import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Flame, Sparkles, ArrowRight, Stethoscope, Medi as MediIcon, Eye, FileText, Target, GraduationCap, Calendar
} from '../components/Icons.jsx';
import { segments, getSegments, setSegments, setDiscipline, disciplines } from '../lib/segment.js';
import { useProfile } from '../lib/useProfile.js';
import kunalPhoto from '../assets/kunal.png';
import SevenReasons from '../components/SevenReasons.jsx';

export default function Overview() {
  const { discipline, copy } = useProfile();
  const [segs, setSegs] = useState(() => getSegments());
  const [pickerOpen, setPickerOpen] = useState(() => getSegments().length === 0);

  useEffect(() => { setSegments(segs.map((s) => s.id)); }, [segs]);
  const toggleSegment = (id) => {
    setSegs((cur) => {
      const exists = cur.find((s) => s.id === id);
      return exists ? cur.filter((s) => s.id !== id) : [...cur, segments[id]];
    });
  };
  const flags = {
    showGamsat: segs.some((s) => s.showGamsat),
    showInternational: segs.some((s) => s.showInternational),
    showReapply: segs.some((s) => s.showReapply),
    showUcat: segs.some((s) => s.showUcat)
  };

  const primary = primaryFor(segs, copy);
  const welcome = segs.length > 0
    ? segs.map((s) => personalisedWelcome(s, copy)).filter((w, i, a) => a.indexOf(w) === i).join(' ')
    : `Tell us where you are and we'll tailor the ${copy.field} trainer.`;

  return (
    <>
      <div className="welcome-kicker">Welcome back</div>
      <h1 className="welcome-h1">Hi there <span style={{ display: 'inline-block' }}>👋</span></h1>
      <p className="welcome-sub">{welcome}</p>

      <Link to="/about" style={credBand}>
        <img src={kunalPhoto} alt="Dr Kunal" width="40" height="40" style={{ borderRadius: '50%', flexShrink: 0, objectFit: 'cover', border: '2px solid #fff', boxShadow: '0 2px 8px rgba(35, 20, 63, 0.15)' }} />
        <span style={{ fontSize: 13, color: 'var(--text-2)', flex: 1, lineHeight: 1.4 }}>
          <strong style={{ color: 'var(--brand-deep)' }}>Built by Dr Kunal</strong> — scored <strong>100% in UCAT</strong>, trained every Medic Mind tutor, mentored <strong>60,000+</strong> applicants to {copy.plural} over 10 years.
        </span>
        <ArrowRight width="14" height="14" style={{ color: 'var(--accent)', flexShrink: 0 }} />
      </Link>

      {pickerOpen ? (
        <SegmentPicker selected={segs.map((s) => s.id)} onToggle={toggleSegment} discipline={discipline} onDiscipline={setDiscipline} onDone={() => setPickerOpen(false)} />
      ) : (
        <SegmentPills segs={segs} discipline={discipline} onChange={() => setPickerOpen(true)} />
      )}

      <section className="card" style={{
        marginBottom: 24,
        background: 'linear-gradient(135deg, var(--feature-from), var(--feature-via), var(--feature-to))',
        color: '#fff', border: 'none', padding: '28px 32px'
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ width: 56, height: 56, background: 'rgba(255,255,255,0.18)', borderRadius: 14, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
            <primary.icon width="28" height="28" />
          </div>
          <div style={{ flex: 1, minWidth: 240 }}>
            <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', opacity: 0.85, marginBottom: 6 }}>Start here</div>
            <h2 style={{ margin: '0 0 4px', fontSize: 22, fontWeight: 700, letterSpacing: '-0.01em' }}>{primary.title}</h2>
            <p style={{ margin: 0, color: '#e9d5ff', fontSize: 14.5 }}>{primary.body}</p>
          </div>
          <Link to={primary.to} className="btn" style={{ background: '#fff', color: 'var(--brand-deep)' }}>{primary.cta} <ArrowRight width="16" height="16" /></Link>
        </div>
      </section>

      <h2 style={{ margin: '0 0 12px', fontSize: 18 }}>Where you can go</h2>
      <div className="r-grid-3" style={{ marginBottom: 24 }}>
        <ShortcutCard icon={FileText} to="/practice" title="Practice" body={flags.showGamsat ? 'GAMSAT essays, S3 drills, mocks.' : `UCAT drills, mocks${copy.field === 'dentistry' ? ' and dental-specific skills' : ' and skills work'}.`} />
        <ShortcutCard icon={Target} to="/plan" title="Plan" body={flags.showInternational ? 'Score, fee status, international fit, shortlist.' : `Score, grades, ${copy.schoolType} fit, shortlist.`} />
        <ShortcutCard icon={GraduationCap} to="/apply" title="Apply" body={`Personal statement, work experience, interviews${copy.field === 'dentistry' ? ' (dental-specific).' : '.'}`} />
      </div>

      {(flags.showGamsat || flags.showInternational || flags.showReapply || copy.field === 'dentistry') && (
        <section className="card" style={{ marginBottom: 24, padding: 18, background: 'var(--accent-soft)', borderColor: 'rgba(124, 62, 175, 0.25)' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--brand-purple)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>
            <Sparkles width="12" height="12" style={{ verticalAlign: 'middle', marginRight: 4 }} />Tailored for you
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {copy.field === 'dentistry' && <PersonalisedChip to="/where-can-i-apply" label="Dental schools that fit you" />}
            {flags.showGamsat && <PersonalisedChip to="/gamsat" label="GAMSAT prep & timeline" />}
            {flags.showGamsat && <PersonalisedChip to="/gamsat-essay" label="GAMSAT essay sprint" />}
            {flags.showReapply && <PersonalisedChip to="/strategy-audit" label="Strategy audit (re-applicant)" />}
            {flags.showInternational && <PersonalisedChip to="/where-can-i-apply" label="International fee filter" />}
          </div>
        </section>
      )}

      <SevenReasons />

      <Link to="/medi" className="medi-promo">
        <div className="avatar"><MediIcon width="40" height="40" /></div>
        <div>
          <div className="kicker">Ash · your AI tutor</div>
          <p>Ask anything — UCAT strategy, GAMSAT, school choice, personal statement.</p>
          <span className="link">Chat with Ash <ArrowRight width="14" height="14" /></span>
        </div>
      </Link>
    </>
  );
}

const credBand = {
  display: 'flex', alignItems: 'center', gap: 12,
  padding: '10px 14px', marginBottom: 18,
  background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12,
  textDecoration: 'none', boxShadow: 'var(--shadow-card)'
};

function primaryFor(segs, copy) {
  const c = copy || { field: 'medicine', schoolTypePlural: 'medical schools', plural: 'doctors' };
  if (!segs || segs.length === 0) return { icon: Sparkles, title: 'Pick your path', body: `Tell us if you're UK undergrad, gap year, GAMSAT or international (or more than one) and we'll tailor the ${c.field} trainer.`, to: '/practice', cta: 'Browse practice' };
  const has = (flag) => segs.some((s) => s[flag]);
  if (has('showGamsat')) return { icon: FileText, title: 'Run a GAMSAT essay sprint', body: '30 minutes, one prompt, one essay. Mirrors real Section 2 pacing.', to: '/gamsat-essay', cta: 'Start sprint' };
  if (has('showReapply')) return { icon: Sparkles, title: 'Strategy audit', body: "A 30-min audit pinpoints the single biggest gap in last cycle's application.", to: '/strategy-audit', cta: 'Start audit' };
  if (has('showInternational')) return { icon: Target, title: 'See where you can apply', body: `UK ${c.schoolTypePlural}, filtered by your UCAT and international fee status.`, to: '/where-can-i-apply', cta: 'Open tool' };
  return { icon: Eye, title: 'Today\'s drill — Noun / Number Radar', body: 'Train the scan-and-retrieve reflex. Under 60 seconds, real UCAT timing.', to: '/practice', cta: 'Start drill' };
}

function personalisedWelcome(seg, copy) {
  if (seg.showGamsat) return seg.welcome;
  if (seg.showReapply) return seg.welcome;
  if (copy.field === 'dentistry') {
    if (seg.showInternational) return 'International route — UCAT is still required for most UK dental schools, plus extra rules on quotas, IELTS and fee status.';
    return "Let's sharpen the skills behind your UCAT score and your dental-school application.";
  }
  return seg.welcome;
}

function PersonalisedChip({ to, label }) {
  return (
    <Link to={to} style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '7px 13px', borderRadius: 99,
      background: '#fff', border: '1px solid rgba(124, 62, 175, 0.3)',
      color: 'var(--brand-deep)', fontSize: 13, fontWeight: 600,
      textDecoration: 'none', transition: 'transform .15s ease, box-shadow .15s ease'
    }} className="hover-lift">
      {label}<ArrowRight width="13" height="13" />
    </Link>
  );
}

function ShortcutCard({ icon: Icon, to, title, body }) {
  return (
    <Link to={to} className="parents-card" style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}>
      <div className="icon-wrap"><Icon /></div>
      <h3 style={{ margin: '8px 0 6px', fontSize: 17 }}>{title}</h3>
      <p style={{ color: 'var(--text-2)', fontSize: 14, margin: 0 }}>{body}</p>
    </Link>
  );
}

function SegmentPicker({ selected, onToggle, onDone, discipline, onDiscipline }) {
  return (
    <section className="card" style={{ marginBottom: 24, padding: 22 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6, flexWrap: 'wrap', gap: 8 }}>
        <h2 style={{ margin: 0, fontSize: 18 }}>Which path are you on?</h2>
        <button onClick={onDone} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--brand-purple)', fontSize: 13, fontWeight: 600 }}>Done</button>
      </div>
      <p style={{ margin: '0 0 14px', color: 'var(--text-muted)', fontSize: 13 }}>Pick all that apply — international + gap year, undergrad + international etc. all combine.</p>

      <div style={{ display: 'flex', gap: 8, marginBottom: 18, padding: '4px', background: 'var(--card-soft)', borderRadius: 99, width: 'fit-content' }}>
        {Object.entries(disciplines).map(([id, d]) => {
          const active = discipline === id;
          return (
            <button key={id} onClick={() => onDiscipline(id)} style={{
              padding: '8px 16px', borderRadius: 99, cursor: 'pointer', fontFamily: 'inherit',
              border: 'none',
              background: active ? '#fff' : 'transparent',
              color: active ? 'var(--brand-deep)' : 'var(--text-muted)',
              fontWeight: 600, fontSize: 13.5,
              boxShadow: active ? '0 2px 8px rgba(35, 20, 63, 0.12)' : 'none',
              transition: 'all .2s ease',
              display: 'inline-flex', alignItems: 'center', gap: 6
            }}>
              <span>{d.emoji}</span> {d.label}
            </button>
          );
        })}
      </div>

      <div className="r-grid-2">
        {Object.values(segments).map((s) => {
          const isSelected = selected.includes(s.id);
          return (
            <button
              key={s.id}
              onClick={() => onToggle(s.id)}
              style={{
                textAlign: 'left', padding: '14px 16px', cursor: 'pointer',
                border: `2px solid ${isSelected ? 'var(--brand-purple)' : 'var(--border)'}`,
                background: isSelected ? 'var(--accent-soft)' : '#fff', borderRadius: 12,
                display: 'flex', gap: 12, alignItems: 'center',
                transition: 'all 150ms ease'
              }}
            >
              <span style={{ fontSize: 26 }}>{s.emoji}</span>
              <div style={{ flex: 1 }}>
                <strong style={{ fontSize: 14.5, color: 'var(--text)' }}>{s.label}</strong>
                <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 2 }}>{s.sub}</div>
              </div>
              <div style={{
                width: 22, height: 22, borderRadius: 6,
                border: `2px solid ${isSelected ? 'var(--brand-purple)' : 'var(--border-strong)'}`,
                background: isSelected ? 'var(--brand-purple)' : '#fff',
                display: 'grid', placeItems: 'center', flexShrink: 0
              }}>
                {isSelected && <span style={{ color: '#fff', fontSize: 14, fontWeight: 700, lineHeight: 1 }}>✓</span>}
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
}

function SegmentPills({ segs, onChange, discipline }) {
  if (!segs || segs.length === 0) {
    return (
      <div style={{ marginBottom: 18 }}>
        <button onClick={onChange} className="btn btn-secondary">
          <Sparkles width="14" height="14" />Personalise
        </button>
      </div>
    );
  }
  const d = disciplines[discipline] || disciplines.medicine;
  return (
    <div style={{ marginBottom: 18, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', background: 'linear-gradient(135deg, var(--brand-deep), var(--brand-purple))', borderRadius: 99 }}>
        <span style={{ fontSize: 15 }}>{d.emoji}</span>
        <span style={{ fontSize: 13, color: '#fff', fontWeight: 600 }}>{d.label}</span>
      </div>
      {segs.map((s) => (
        <div key={s.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', background: 'var(--accent-soft)', border: '1px solid rgba(124, 62, 175, 0.25)', borderRadius: 99 }}>
          <span style={{ fontSize: 15 }}>{s.emoji}</span>
          <span style={{ fontSize: 13, color: 'var(--brand-deep)', fontWeight: 600 }}>{s.label}</span>
        </div>
      ))}
      <button onClick={onChange} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--brand-purple)', fontSize: 12.5, fontWeight: 600 }}>Change</button>
    </div>
  );
}
